import { phappsRequest } from "./apiService";
import { API_PATHS } from "@/config/apiConstants";

/**
 * Dedupe AdminAuthority:
 * - Concurrent callers share one in-flight request (StrictMode overlap).
 * - A short cooldown skips a second run right after the first completes (StrictMode sequential).
 */
let adminAuthorityInFlight = null;
let adminAuthorityLastCompletedAt = 0;
const ADMIN_AUTHORITY_COOLDOWN_MS = 900;

/** Call when session ends so the next login is not skipped by cooldown. */
export const resetAdminAuthorityCooldown = () => {
  adminAuthorityLastCompletedAt = 0;
};

export const checkPHAdmin = async () => {
  if (adminAuthorityInFlight) {
    await adminAuthorityInFlight;
    return;
  }
  const now = Date.now();
  if (
    adminAuthorityLastCompletedAt > 0 &&
    now - adminAuthorityLastCompletedAt < ADMIN_AUTHORITY_COOLDOWN_MS &&
    sessionStorage.getItem("IsPHAdmin") != null
  ) {
    return;
  }

  adminAuthorityInFlight = (async () => {
    try {
      const response = await phappsRequest(API_PATHS.IS_PH_ADMIN, "GET");
      const phRaw = response?.IsPHAdmin ?? response?.isPHAdmin;
      const hubRaw = response?.IsHubMedAdmin ?? response?.isHubMedAdmin;
      const zephyrRaw = response?.IsZephyrAdmin ?? response?.isZephyrAdmin;
      const holaRaw = response?.IsHolaAdmin ?? response?.isHolaAdmin;
      const medmateRaw = response?.IsMedmateAdmin ?? response?.isMedmateAdmin;
      const confRaw = response?.is_conferences_user;
      if (response && (typeof phRaw === "boolean" || typeof hubRaw === "boolean")) {
        sessionStorage.setItem("IsPHAdmin", String(phRaw === true));
        sessionStorage.setItem("IsHubMedAdmin", String(hubRaw === true));
        sessionStorage.setItem("IsZephyrAdmin", String(zephyrRaw === true));
        sessionStorage.setItem("IsHolaAdmin", String(holaRaw === true));
        sessionStorage.setItem("IsMedmateAdmin", String(medmateRaw === true));
        sessionStorage.setItem("is_conferences_user", String(confRaw === true));
      }
    } catch (err) {
      console.error("Error checking PH Admin status:", err);
      sessionStorage.setItem("IsPHAdmin", "false");
      sessionStorage.setItem("IsHubMedAdmin", "false");
      sessionStorage.setItem("IsZephyrAdmin", "false");
      sessionStorage.setItem("IsHolaAdmin", "false");
      sessionStorage.setItem("IsMedmateAdmin", "false");
      sessionStorage.setItem("is_conferences_user", "false");
    } finally {
      adminAuthorityLastCompletedAt = Date.now();
      adminAuthorityInFlight = null;
    }
  })();
  await adminAuthorityInFlight;
};

/** HubMed admin without PH admin and no other provider roles. */
export const isHubMedOnlyUser = () =>
  sessionStorage.getItem("IsHubMedAdmin") === "true" &&
  sessionStorage.getItem("IsHolaAdmin") !== "true" &&
  sessionStorage.getItem("IsMedmateAdmin") !== "true" &&
  sessionStorage.getItem("IsZephyrAdmin") !== "true" &&
  sessionStorage.getItem("IsPHAdmin") !== "true";

/** Hola admin without PH admin and no other provider roles. */
export const isHolaOnlyUser = () =>
  sessionStorage.getItem("IsHolaAdmin") === "true" &&
  sessionStorage.getItem("IsHubMedAdmin") !== "true" &&
  sessionStorage.getItem("IsMedmateAdmin") !== "true" &&
  sessionStorage.getItem("IsZephyrAdmin") !== "true" &&
  sessionStorage.getItem("IsPHAdmin") !== "true";

/** Medmate admin without PH admin and no other provider roles. */
export const isMedmateOnlyUser = () =>
  sessionStorage.getItem("IsMedmateAdmin") === "true" &&
  sessionStorage.getItem("IsHubMedAdmin") !== "true" &&
  sessionStorage.getItem("IsHolaAdmin") !== "true" &&
  sessionStorage.getItem("IsZephyrAdmin") !== "true" &&
  sessionStorage.getItem("IsPHAdmin") !== "true";

/** Zephyr admin without PH admin and no other provider roles. */
export const isZephyrOnlyUser = () =>
  sessionStorage.getItem("IsZephyrAdmin") === "true" &&
  sessionStorage.getItem("IsHubMedAdmin") !== "true" &&
  sessionStorage.getItem("IsHolaAdmin") !== "true" &&
  sessionStorage.getItem("IsMedmateAdmin") !== "true" &&
  sessionStorage.getItem("IsPHAdmin") !== "true";

/**
 * A "provider-only" user has one or more provider admin roles
 * (HubMed / Hola / Medmate / Zephyr) but is NOT a PH Admin.
 * They should NOT see the pharmacy Overview or Workflow items.
 */
export const isProviderOnlyUser = () =>
  sessionStorage.getItem("IsPHAdmin") !== "true" &&
  (sessionStorage.getItem("IsHubMedAdmin") === "true" ||
    sessionStorage.getItem("IsHolaAdmin") === "true" ||
    sessionStorage.getItem("IsMedmateAdmin") === "true" ||
    sessionStorage.getItem("IsZephyrAdmin") === "true");

/** Returns the first provider path for a provider-only multi-role user. */
export const getProviderDefaultPath = () => {
  if (sessionStorage.getItem("IsHubMedAdmin") === "true") return "/hubmed-admin/overview";
  if (sessionStorage.getItem("IsHolaAdmin") === "true") return "/hola-admin/overview";
  if (sessionStorage.getItem("IsMedmateAdmin") === "true") return "/medmate-admin/overview";
  if (sessionStorage.getItem("IsZephyrAdmin") === "true") return "/zephyr-admin/overview";
  return "/overview";
};

/**
 * True if the user can access Conferences:
 * PH Admins always can; others need is_conferences_user = true.
 */
export const canAccessConferences = () =>
  sessionStorage.getItem("IsPHAdmin") === "true" ||
  sessionStorage.getItem("is_conferences_user") === "true";

export const getDefaultAuthenticatedHomePath = () => {
  if (isHubMedOnlyUser()) return "/hubmed-admin/overview";
  if (isHolaOnlyUser()) return "/hola-admin/overview";
  if (isMedmateOnlyUser()) return "/medmate-admin/overview";
  if (isZephyrOnlyUser()) return "/zephyr-admin/overview";
  if (isProviderOnlyUser()) return getProviderDefaultPath();
  if (sessionStorage.getItem("IsPHAdmin") === "true") {
    return "/admin-functions/overview";
  }
  return "/manager-functions/overview";
};
